import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabaseClient";
import { getTournamentTheme } from "../../../../styles/themes";

export default function EditScores({
    tournament,
    setTournament,
    setPrevStandings,
    setActiveTab,
    isAdmin,
}) {
    const [saveMessage, setSaveMessage] = useState("");
    const [bruttoValues, setBruttoValues] = useState({});
    const [teamHandicapDetails, setTeamHandicapDetails] = useState({});
    const [miniGameValues, setMiniGameValues] = useState({});
    const [activeRoundIndex, setActiveRoundIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const theme = getTournamentTheme(tournament?.theme_key);

    useEffect(() => {
        const initialBruttos = {};

        tournament?.playerData?.forEach((player) => {
            tournament.rounds.forEach((round, roundIdx) => {
                if (
                    round.modes?.[0]?.toLowerCase() ===
                    "handicap-justerat slagspel"
                ) {
                    const netto = player.scores?.[roundIdx];
                    const fullHcp = parseFloat(player.handicap);
                    const holes = round.holes ?? 18;
                    const hcp = holes === 9 ? fullHcp / 2 : fullHcp;

                    if (!isNaN(netto) && !isNaN(hcp)) {
                        initialBruttos[`${player.id}-${roundIdx}`] =
                            Math.round(netto + hcp);
                    }
                }

                if (round.scrambleOptions?.scrambleWithHandicap) {
                    const teams = round.teams || [];

                    teams.forEach(([id1, id2]) => {
                        const teamLeader = tournament.playerData.find(
                            (playerItem) => playerItem.id === id1
                        );
                        const teamScore =
                            teamLeader?.scores?.[roundIdx];

                        const handicapDetails =
                            teamHandicapDetails[`${id1}-${id2}`] ??
                            teamHandicapDetails[`${id2}-${id1}`];
                        const roundDeduction =
                            handicapDetails?.roundDeduction ?? 0;

                        if (
                            teamScore != null &&
                            teamScore !== 0 &&
                            !isNaN(roundDeduction)
                        ) {
                            initialBruttos[`${id1}-${roundIdx}`] =
                                teamScore + roundDeduction;
                        }
                    });
                }
            });
        });

        setBruttoValues(initialBruttos);
    }, [tournament, teamHandicapDetails]);

    const getMiniGameValueKey = (roundIdx, miniGame, playerId) =>
        `${roundIdx}-${miniGame.id || miniGame.name}-${playerId}`;

    useEffect(() => {
        setMiniGameValues((currentValues) => {
            const nextValues = { ...currentValues };

            tournament.miniGames?.forEach((miniGame) => {
                tournament.rounds.forEach((_, roundIdx) => {
                    tournament.playerData.forEach((player) => {
                        const key = getMiniGameValueKey(
                            roundIdx,
                            miniGame,
                            player.id
                        );

                        if (!(key in nextValues)) {
                            const storedValue =
                                player?.miniGameScores?.[roundIdx]?.[
                                    miniGame.name
                                ];

                            nextValues[key] =
                                storedValue !== null &&
                                storedValue !== undefined &&
                                storedValue !== "" &&
                                !isNaN(Number(storedValue))
                                    ? Math.max(0, Number(storedValue))
                                    : 0;
                        }
                    });
                });
            });

            return nextValues;
        });
    }, [tournament]);

    const setMiniGameValue = (
        roundIdx,
        miniGame,
        playerId,
        value
    ) => {
        const key = getMiniGameValueKey(
            roundIdx,
            miniGame,
            playerId
        );
        const parsedValue = parseInt(value, 10);

        setMiniGameValues((currentValues) => ({
            ...currentValues,
            [key]: isNaN(parsedValue)
                ? 0
                : Math.max(0, parsedValue),
        }));
    };

    const changeMiniGameValue = (
        roundIdx,
        miniGame,
        playerId,
        change
    ) => {
        const key = getMiniGameValueKey(
            roundIdx,
            miniGame,
            playerId
        );

        setMiniGameValues((currentValues) => ({
            ...currentValues,
            [key]: Math.max(
                0,
                Number(currentValues[key] || 0) + change
            ),
        }));
    };

    const resetMiniGame = (roundIdx, miniGame) => {
        setMiniGameValues((currentValues) => {
            const nextValues = { ...currentValues };

            tournament.playerData.forEach((player) => {
                const key = getMiniGameValueKey(
                    roundIdx,
                    miniGame,
                    player.id
                );
                nextValues[key] = 0;
            });

            return nextValues;
        });
    };

    useEffect(() => {
        let anyUpdated = false;
        const updatedRounds = [...tournament.rounds];

        updatedRounds.forEach((round, idx) => {
            const isScramble = round.modes?.[0]
                ?.toLowerCase()
                .includes("scramble");
            const pairing =
                round.scrambleOptions?.pairing || "position";
            const hasCustomPairs =
                pairing === "custom" &&
                round.scrambleOptions?.customTeams;
            const existingTeams = round.teams || [];

            if (
                isScramble &&
                hasCustomPairs &&
                existingTeams.length === 0
            ) {
                const used = new Set();
                const customPairs =
                    round.scrambleOptions.customTeams;
                const customTeams = [];

                for (const [player1, player2] of Object.entries(
                    customPairs
                )) {
                    if (
                        !used.has(player1) &&
                        !used.has(player2)
                    ) {
                        customTeams.push([player1, player2]);
                        used.add(player1);
                        used.add(player2);
                    }
                }

                updatedRounds[idx] = {
                    ...round,
                    teams: customTeams,
                };
                anyUpdated = true;
                return;
            }

            if (
                isScramble &&
                pairing === "position" &&
                existingTeams.length === 0 &&
                idx > 0
            ) {
                const hasScores = tournament.playerData.some(
                    (player) =>
                        typeof player.scores?.[idx - 1] ===
                            "number" &&
                        player.scores[idx - 1] > 0
                );

                if (hasScores) {
                    const generated = generateTeamsForRound(
                        round,
                        idx,
                        tournament.playerData,
                        tournament
                    );

                    updatedRounds[idx] = {
                        ...round,
                        teams: generated,
                    };
                    anyUpdated = true;
                }
            }
        });

        if (anyUpdated) {
            setTournament({
                ...tournament,
                rounds: updatedRounds,
            });
        }
        // Körs när komponenten öppnas. Lag skapas därefter i tournament-state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const details = {};

        tournament.rounds.forEach((round) => {
            if (
                round.scrambleOptions?.scrambleWithHandicap
            ) {
                const teams = round.teams || [];

                teams.forEach(([id1, id2]) => {
                    const player1 =
                        tournament.playerData.find(
                            (player) => player.id === id1
                        );
                    const player2 =
                        tournament.playerData.find(
                            (player) => player.id === id2
                        );

                    if (player1 && player2) {
                        const low = Math.min(
                            player1.handicap ?? 0,
                            player2.handicap ?? 0
                        );
                        const high = Math.max(
                            player1.handicap ?? 0,
                            player2.handicap ?? 0
                        );
                        const pctLow =
                            round.scrambleOptions.lowPct ?? 0;
                        const pctHigh =
                            round.scrambleOptions.highPct ?? 0;

                        const rawTeamHandicap =
                            low * (pctLow / 100) +
                            high * (pctHigh / 100);
                        const fullTeamHandicap =
                            Math.round(rawTeamHandicap);
                        const roundDeduction = Math.round(
                            (round.holes ?? 18) === 9
                                ? rawTeamHandicap / 2
                                : rawTeamHandicap
                        );

                        details[`${id1}-${id2}`] = {
                            fullTeamHandicap,
                            roundDeduction,
                        };
                    }
                });
            }
        });

        setTeamHandicapDetails(details);
    }, [tournament]);

    useEffect(() => {
        if (
            activeRoundIndex >
            tournament.rounds.length - 1
        ) {
            setActiveRoundIndex(
                Math.max(0, tournament.rounds.length - 1)
            );
        }
    }, [activeRoundIndex, tournament.rounds.length]);

    const generateTeamsForRound = (
        round,
        roundIndex,
        players,
        tournamentData
    ) => {
        const pairingType =
            round.scrambleOptions?.pairing || "position";

        const pairBy = (group, key = "total") => {
            const sorted = [...group].sort(
                (a, b) => (a[key] ?? 0) - (b[key] ?? 0)
            );
            const teams = [];
            let left = 0;
            let right = sorted.length - 1;

            while (left < right) {
                teams.push([
                    sorted[left++].id,
                    sorted[right--].id,
                ]);
            }

            if (left === right) {
                teams.push([sorted[left].id]);
            }

            return teams;
        };

        const hasAssignments =
            tournamentData.team_data?.assignments &&
            Object.keys(
                tournamentData.team_data.assignments
            ).length > 0;

        if (hasAssignments) {
            const assignments =
                tournamentData.team_data.assignments;
            const red = [];
            const green = [];

            players.forEach((player) => {
                const playerData = {
                    ...player,
                    total: player.scores
                        .slice(0, roundIndex)
                        .reduce(
                            (sum, score) =>
                                sum + (score || 0),
                            0
                        ),
                };

                const team = assignments[player.id];

                if (team === "red") {
                    red.push(playerData);
                } else if (team === "green") {
                    green.push(playerData);
                }
            });

            return [
                ...pairBy(
                    red,
                    pairingType === "handicap"
                        ? "handicap"
                        : "total"
                ),
                ...pairBy(
                    green,
                    pairingType === "handicap"
                        ? "handicap"
                        : "total"
                ),
            ];
        }

        const withTotals = players.map((player) => ({
            ...player,
            total: player.scores
                .slice(0, roundIndex)
                .reduce(
                    (sum, score) => sum + (score || 0),
                    0
                ),
        }));

        return pairBy(
            withTotals,
            pairingType === "handicap"
                ? "handicap"
                : "total"
        );
    };

    const getPlayerRoundHandicap = (player, round) => {
        const fullHandicap = parseFloat(player.handicap);

        if (isNaN(fullHandicap)) {
            return 0;
        }

        return (round.holes ?? 18) === 9
            ? fullHandicap / 2
            : fullHandicap;
    };

    const formatHandicap = (value) => {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return "0";
        }

        return Number.isInteger(numericValue)
            ? String(numericValue)
            : numericValue.toFixed(1);
    };

    const getRoundProgress = (round, roundIdx) => {
        const isScramble = round.modes?.[0]
            ?.toLowerCase()
            .includes("scramble");

        if (isScramble) {
            const teams = round.teams || [];

            const completed = teams.filter((team) =>
                team.some((playerId) => {
                    const player =
                        tournament.playerData.find(
                            (candidate) =>
                                candidate.id === playerId
                        );

                    return (
                        typeof player?.scores?.[roundIdx] ===
                            "number" &&
                        player.scores[roundIdx] > 0
                    );
                })
            ).length;

            return {
                completed,
                total: teams.length,
                label: "lag",
            };
        }

        const completed =
            tournament.playerData.filter(
                (player) =>
                    typeof player.scores?.[roundIdx] ===
                        "number" &&
                    player.scores[roundIdx] > 0
            ).length;

        return {
            completed,
            total: tournament.playerData.length,
            label: "spelare",
        };
    };

    const focusNextInput = (event) => {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();

        const form = event.currentTarget.form;

        if (!form) {
            return;
        }

        const visibleInputs = Array.from(
            form.querySelectorAll(
                '[data-score-entry="true"]'
            )
        ).filter(
            (input) =>
                input.offsetParent !== null &&
                !input.disabled &&
                !input.readOnly
        );

        const currentIndex = visibleInputs.indexOf(
            event.currentTarget
        );
        const nextInput =
            visibleInputs[currentIndex + 1];

        if (nextInput) {
            nextInput.focus();
            nextInput.select();
        } else {
            event.currentTarget.blur();
        }
    };

    const commonInputProps = {
        type: "number",
        inputMode: "numeric",
        pattern: "[0-9]*",
        min: "0",
        step: "1",
        enterKeyHint: "next",
        "data-score-entry": "true",
        onFocus: (event) => event.currentTarget.select(),
        onKeyDown: focusNextInput,
        onWheel: (event) => event.currentTarget.blur(),
    };

    const inputClassName = `${theme.section} ${theme.borderStrong} ${theme.textPrimary} h-12 w-full rounded-xl border px-3 text-center text-base font-semibold tabular-nums outline-none transition focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/20 disabled:cursor-not-allowed disabled:opacity-60`;

    const readOnlyInputClassName = `${theme.sectionMuted} ${theme.border} ${theme.textSecondary} h-12 w-full cursor-not-allowed rounded-xl border px-3 text-center text-base font-semibold tabular-nums`;

    const handleSave = async (event) => {
        event.preventDefault();

        if (!isAdmin || isSaving) {
            return;
        }

        setIsSaving(true);
        setSaveMessage("");

        const currentSorted = [
            ...tournament.playerData,
        ].sort((a, b) => {
            const totalA = a.scores.reduce(
                (sum, value) => sum + (value || 0),
                0
            );
            const totalB = b.scores.reduce(
                (sum, value) => sum + (value || 0),
                0
            );

            return totalA - totalB;
        });

        setPrevStandings(
            currentSorted.map((player) => player.id)
        );

        const formData = new FormData(event.currentTarget);
        const updatedScores = [];
        const updatedMiniGameScores = [];

        const updatedPlayers = tournament.playerData.map(
            (player) => {
                const scores = tournament.rounds.map(
                    (round, roundIdx) => {
                        const isScramble =
                            round.modes?.[0]
                                ?.toLowerCase()
                                .includes("scramble");
                        const teams = round.teams || [];
                        let value = null;

                        if (isScramble) {
                            const team = teams.find(
                                (teamItem) =>
                                    teamItem.includes(player.id)
                            );
                            const teamLeaderId = team?.[0];

                            value = formData.get(
                                `${teamLeaderId}-${roundIdx}`
                            );
                        } else if (
                            round.modes?.[0]?.toLowerCase() ===
                            "handicap-justerat slagspel"
                        ) {
                            value = formData.get(
                                `netto-${player.id}-${roundIdx}`
                            );
                        } else {
                            value = formData.get(
                                `${player.id}-${roundIdx}`
                            );
                        }

                        const score =
                            value !== null && value !== ""
                                ? parseInt(value, 10)
                                : 0;

                        updatedScores.push({
                            round_id:
                                tournament.rounds[roundIdx]
                                    .id,
                            player_id: player.id,
                            score,
                        });

                        return score;
                    }
                );

                const miniGameScores = {};

                tournament.miniGames?.forEach(
                    (miniGame) => {
                        tournament.rounds.forEach(
                            (round, roundIdx) => {
                                const key = `mini-${roundIdx}-${miniGame.name}-${player.id}`;
                                const value =
                                    formData.get(key);

                                if (
                                    !miniGameScores[roundIdx]
                                ) {
                                    miniGameScores[
                                        roundIdx
                                    ] = {};
                                }

                                if (
                                    value !== null &&
                                    value !== ""
                                ) {
                                    const parsedValue =
                                        parseInt(value, 10);

                                    miniGameScores[
                                        roundIdx
                                    ][miniGame.name] =
                                        parsedValue;

                                    if (parsedValue > 0) {
                                        updatedMiniGameScores.push(
                                            {
                                                mini_game_id:
                                                    miniGame.id,
                                                round_id:
                                                    round.id,
                                                player_id:
                                                    player.id,
                                                value: parsedValue,
                                            }
                                        );
                                    }
                                }
                            }
                        );
                    }
                );

                return {
                    ...player,
                    scores,
                    miniGameScores,
                };
            }
        );

        const updatedTournament = {
            ...tournament,
            playerData: updatedPlayers,
        };

        try {
            const roundIds = tournament.rounds.map(
                (round) => round.id
            );
            const playerIds =
                tournament.playerData.map(
                    (player) => player.id
                );

            const { error: deleteScoresError } =
                await supabase
                    .from("scores")
                    .delete()
                    .in("round_id", roundIds)
                    .in("player_id", playerIds);

            if (deleteScoresError) {
                throw deleteScoresError;
            }

            if (updatedScores.length > 0) {
                const { error: insertScoresError } =
                    await supabase
                        .from("scores")
                        .insert(updatedScores);

                if (insertScoresError) {
                    throw insertScoresError;
                }
            }

            const { error: deleteMiniGameScoresError } =
                await supabase
                    .from("mini_game_scores")
                    .delete()
                    .in("round_id", roundIds)
                    .in("player_id", playerIds);

            if (deleteMiniGameScoresError) {
                throw deleteMiniGameScoresError;
            }

            if (updatedMiniGameScores.length > 0) {
                const {
                    error: insertMiniGameScoresError,
                } = await supabase
                    .from("mini_game_scores")
                    .insert(updatedMiniGameScores);

                if (insertMiniGameScoresError) {
                    throw insertMiniGameScoresError;
                }
            }

            setTournament(updatedTournament);
            setSaveMessage("Resultaten är sparade.");

            window.setTimeout(() => {
                setSaveMessage("");
            }, 3000);

            setActiveTab("standings");
        } catch (error) {
            console.error(
                "Kunde inte spara resultaten:",
                error
            );
            setSaveMessage(
                "Resultaten kunde inte sparas. Försök igen."
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section
            className={`${theme.section} ${theme.border} rounded-2xl border shadow-sm`}
        >
            <div
                className={`${theme.border} flex flex-col gap-3 rounded-t-2xl border-b px-4 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6`}
            >
                <div>
                    <p
                        className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.24em]`}
                    >
                        Administration
                    </p>

                    <h2
                        className={`${theme.heading} mt-1 text-xl font-semibold tracking-tight sm:text-2xl`}
                    >
                        Redigera resultat
                    </h2>

                    <p
                        className={`${theme.textSecondary} mt-1 max-w-xl text-xs leading-5 sm:text-sm`}
                    >
                        Välj en runda och registrera
                        resultat för en spelare eller ett
                        lag i taget.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <span
                        className={`${theme.primarySoft} ${theme.heading} rounded-full px-3 py-1.5 text-xs font-semibold`}
                    >
                        {tournament.rounds.length}{" "}
                        {tournament.rounds.length === 1
                            ? "runda"
                            : "rundor"}
                    </span>

                    <span
                        className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} rounded-full border px-3 py-1.5 text-xs font-medium`}
                    >
                        {tournament.playerData.length} spelare
                    </span>

                    {tournament.miniGames?.length > 0 && (
                        <span
                            className={`${theme.accentSoft} ${theme.accent} rounded-full px-3 py-1.5 text-xs font-semibold`}
                        >
                            {tournament.miniGames.length} mini{" "}
                            {tournament.miniGames.length ===
                            1
                                ? "game"
                                : "games"}
                        </span>
                    )}
                </div>
            </div>

            <form onSubmit={handleSave}>
                {tournament.rounds.length > 1 && (
                    <div
                        className={`${theme.sectionMuted} ${theme.border} border-b px-3 py-3 sm:px-5`}
                    >
                        <p
                            className={`${theme.textSecondary} mb-2 px-1 text-[9px] font-semibold uppercase tracking-[0.2em]`}
                        >
                            Välj runda
                        </p>

                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {tournament.rounds.map(
                                (round, roundIdx) => {
                                    const isActive =
                                        activeRoundIndex ===
                                        roundIdx;
                                    const progress =
                                        getRoundProgress(
                                            round,
                                            roundIdx
                                        );

                                    return (
                                        <button
                                            key={
                                                round.id ??
                                                roundIdx
                                            }
                                            type="button"
                                            onClick={() =>
                                                setActiveRoundIndex(
                                                    roundIdx
                                                )
                                            }
                                            className={`min-w-[150px] flex-1 rounded-xl border px-3 py-2.5 text-left transition sm:min-w-[175px] ${
                                                isActive
                                                    ? `${theme.primary} ${theme.textOnPrimary} border-transparent shadow-sm`
                                                    : `${theme.section} ${theme.border} ${theme.textPrimary} hover:bg-black/[0.025]`
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p
                                                        className={`text-[9px] font-semibold uppercase tracking-[0.18em] ${
                                                            isActive
                                                                ? "text-white/60"
                                                                : theme.textSecondary
                                                        }`}
                                                    >
                                                        Runda{" "}
                                                        {roundIdx +
                                                            1}
                                                    </p>

                                                    <p className="mt-0.5 truncate text-sm font-semibold">
                                                        {round.name ||
                                                            `Runda ${
                                                                roundIdx +
                                                                1
                                                            }`}
                                                    </p>
                                                </div>

                                                <span
                                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                                                        isActive
                                                            ? "bg-white/10 text-white/80"
                                                            : `${theme.primarySoft} ${theme.heading}`
                                                    }`}
                                                >
                                                    {
                                                        progress.completed
                                                    }
                                                    /
                                                    {
                                                        progress.total
                                                    }
                                                </span>
                                            </div>

                                            <p
                                                className={`mt-1 truncate text-[10px] ${
                                                    isActive
                                                        ? "text-white/55"
                                                        : theme.textSecondary
                                                }`}
                                            >
                                                {round.modes?.[0] ||
                                                    "Spelform saknas"}{" "}
                                                ·{" "}
                                                {round.holes ??
                                                    18}{" "}
                                                hål
                                            </p>
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>
                )}

                <div className="p-3 sm:p-5">
                    {tournament.rounds.map(
                        (round, roundIdx) => {
                            const isActive =
                                activeRoundIndex === roundIdx;
                            const isScramble =
                                round.modes?.[0]
                                    ?.toLowerCase()
                                    .includes("scramble");
                            const teams =
                                round.teams || [];
                            const progress =
                                getRoundProgress(
                                    round,
                                    roundIdx
                                );

                            return (
                                <article
                                    key={
                                        round.id ?? roundIdx
                                    }
                                    className={
                                        isActive
                                            ? "block"
                                            : "hidden"
                                    }
                                >
                                    <div
                                        className={`${theme.sectionMuted} ${theme.border} mb-3 rounded-xl border px-4 py-3 sm:flex sm:items-center sm:justify-between`}
                                    >
                                        <div className="min-w-0">
                                            <p
                                                className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.2em]`}
                                            >
                                                Runda{" "}
                                                {roundIdx + 1}
                                            </p>

                                            <h3
                                                className={`${theme.heading} mt-0.5 break-words text-base font-semibold sm:text-lg`}
                                            >
                                                {round.name ||
                                                    `Runda ${
                                                        roundIdx +
                                                        1
                                                    }`}
                                            </h3>
                                        </div>

                                        <div className="mt-2 flex flex-wrap items-center gap-2 sm:mt-0 sm:justify-end">
                                            <span
                                                className={`${theme.primarySoft} ${theme.heading} rounded-full px-2.5 py-1 text-[10px] font-semibold`}
                                            >
                                                {round.modes?.[0] ||
                                                    "Spelform saknas"}
                                            </span>

                                            <span
                                                className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-2.5 py-1 text-[10px] font-medium`}
                                            >
                                                {round.holes ??
                                                    18}{" "}
                                                hål
                                            </span>

                                            <span
                                                className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-2.5 py-1 text-[10px] font-medium`}
                                            >
                                                {
                                                    progress.completed
                                                }
                                                /
                                                {
                                                    progress.total
                                                }{" "}
                                                {progress.label}
                                            </span>
                                        </div>
                                    </div>

                                    {!isScramble && (
                                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                            {tournament.playerData.map(
                                                (
                                                    player,
                                                    playerIdx
                                                ) => {
                                                    const isHandicapAdjusted =
                                                        round.modes?.[0]?.toLowerCase() ===
                                                        "handicap-justerat slagspel";
                                                    const playerHcp =
                                                        getPlayerRoundHandicap(
                                                            player,
                                                            round
                                                        );
                                                    const bruttoKey = `${player.id}-${roundIdx}`;
                                                    const brutto =
                                                        bruttoValues[
                                                            bruttoKey
                                                        ];
                                                    const netto =
                                                        brutto !==
                                                            "" &&
                                                        brutto !=
                                                            null &&
                                                        !isNaN(
                                                            Number(
                                                                brutto
                                                            )
                                                        )
                                                            ? Math.max(
                                                                  0,
                                                                  Math.round(
                                                                      Number(
                                                                          brutto
                                                                      ) -
                                                                          playerHcp
                                                                  )
                                                              )
                                                            : "";

                                                    return (
                                                        <section
                                                            key={
                                                                player.id
                                                            }
                                                            className={`${theme.tableRow} ${theme.border} rounded-xl border p-3 shadow-[0_2px_8px_rgba(31,45,38,0.04)] sm:p-4`}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <p
                                                                        className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}
                                                                    >
                                                                        Spelare{" "}
                                                                        {playerIdx +
                                                                            1}
                                                                    </p>

                                                                    <h4
                                                                        className={`${theme.textPrimary} mt-0.5 truncate text-base font-semibold`}
                                                                    >
                                                                        {
                                                                            player.name
                                                                        }
                                                                    </h4>
                                                                </div>

                                                                {player.handicap !=
                                                                    null && (
                                                                    <span
                                                                        className={`${theme.primarySoft} ${theme.heading} shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold`}
                                                                    >
                                                                        HCP{" "}
                                                                        {formatHandicap(
                                                                            playerHcp
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="mt-4">
                                                                <label
                                                                    className={`${theme.textSecondary} mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.16em]`}
                                                                    htmlFor={
                                                                        isHandicapAdjusted
                                                                            ? `brutto-${player.id}-${roundIdx}`
                                                                            : `score-${player.id}-${roundIdx}`
                                                                    }
                                                                >
                                                                    {isHandicapAdjusted
                                                                        ? "Brutto och netto"
                                                                        : "Antal slag"}
                                                                </label>

                                                                {isHandicapAdjusted ? (
                                                                    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                                                                        <div>
                                                                            <input
                                                                                {...commonInputProps}
                                                                                id={`brutto-${player.id}-${roundIdx}`}
                                                                                name={`brutto-${player.id}-${roundIdx}`}
                                                                                placeholder="Brutto"
                                                                                value={
                                                                                    brutto ??
                                                                                    ""
                                                                                }
                                                                                disabled={
                                                                                    !isAdmin
                                                                                }
                                                                                aria-label={`Bruttoresultat för ${player.name}`}
                                                                                className={
                                                                                    inputClassName
                                                                                }
                                                                                onChange={(
                                                                                    event
                                                                                ) => {
                                                                                    const parsed =
                                                                                        parseInt(
                                                                                            event
                                                                                                .target
                                                                                                .value,
                                                                                            10
                                                                                        );

                                                                                    setBruttoValues(
                                                                                        (
                                                                                            current
                                                                                        ) => ({
                                                                                            ...current,
                                                                                            [bruttoKey]:
                                                                                                isNaN(
                                                                                                    parsed
                                                                                                )
                                                                                                    ? ""
                                                                                                    : parsed,
                                                                                        })
                                                                                    );
                                                                                }}
                                                                            />
                                                                            <p
                                                                                className={`${theme.textSecondary} mt-1 text-center text-[9px]`}
                                                                            >
                                                                                Brutto
                                                                            </p>
                                                                        </div>

                                                                        <span
                                                                            className={`${theme.primarySoft} ${theme.positive} rounded-full px-2 py-1 text-[10px] font-semibold whitespace-nowrap`}
                                                                        >
                                                                            −
                                                                            {formatHandicap(
                                                                                playerHcp
                                                                            )}
                                                                        </span>

                                                                        <div>
                                                                            <input
                                                                                id={`netto-${player.id}-${roundIdx}`}
                                                                                name={`netto-${player.id}-${roundIdx}`}
                                                                                type="number"
                                                                                readOnly
                                                                                value={
                                                                                    netto
                                                                                }
                                                                                aria-label={`Nettoresultat för ${player.name}`}
                                                                                className={
                                                                                    readOnlyInputClassName
                                                                                }
                                                                            />
                                                                            <p
                                                                                className={`${theme.textSecondary} mt-1 text-center text-[9px]`}
                                                                            >
                                                                                Netto
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <input
                                                                        {...commonInputProps}
                                                                        id={`score-${player.id}-${roundIdx}`}
                                                                        name={`${player.id}-${roundIdx}`}
                                                                        placeholder="Slag"
                                                                        defaultValue={
                                                                            player
                                                                                .scores?.[
                                                                                roundIdx
                                                                            ] ??
                                                                            ""
                                                                        }
                                                                        disabled={
                                                                            !isAdmin
                                                                        }
                                                                        aria-label={`Resultat för ${player.name}`}
                                                                        className={
                                                                            inputClassName
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        </section>
                                                    );
                                                }
                                            )}
                                        </div>
                                    )}

                                    {isScramble &&
                                        teams.length > 0 && (
                                            <div className="grid gap-3 lg:grid-cols-2">
                                                {teams.map(
                                                    (
                                                        team,
                                                        teamIdx
                                                    ) => {
                                                        const players =
                                                            team
                                                                .map(
                                                                    (
                                                                        playerId
                                                                    ) =>
                                                                        tournament.playerData.find(
                                                                            (
                                                                                player
                                                                            ) =>
                                                                                player.id ===
                                                                                playerId
                                                                        )
                                                                )
                                                                .filter(
                                                                    Boolean
                                                                );

                                                        const teamName =
                                                            players
                                                                .map(
                                                                    (
                                                                        player
                                                                    ) =>
                                                                        player.name
                                                                )
                                                                .join(
                                                                    " & "
                                                                ) ||
                                                            "Okänt lag";

                                                        const teamLeaderId =
                                                            team[0];

                                                        const teamScore =
                                                            players.find(
                                                                (
                                                                    player
                                                                ) =>
                                                                    player
                                                                        .scores?.[
                                                                        roundIdx
                                                                    ] !=
                                                                    null
                                                            )
                                                                ?.scores?.[
                                                                roundIdx
                                                            ] ??
                                                            "";

                                                        const handicapDetails =
                                                            teamHandicapDetails[
                                                                `${team[0]}-${team[1]}`
                                                            ] ??
                                                            teamHandicapDetails[
                                                                `${team[1]}-${team[0]}`
                                                            ];
                                                        const fullTeamHandicap =
                                                            handicapDetails?.fullTeamHandicap ??
                                                            0;
                                                        const roundDeduction =
                                                            handicapDetails?.roundDeduction ??
                                                            0;

                                                        const bruttoKey = `${teamLeaderId}-${roundIdx}`;
                                                        const brutto =
                                                            bruttoValues[
                                                                bruttoKey
                                                            ];

                                                        const netto =
                                                            brutto !==
                                                                "" &&
                                                            brutto !=
                                                                null &&
                                                            !isNaN(
                                                                Number(
                                                                    brutto
                                                                )
                                                            )
                                                                ? Math.max(
                                                                      0,
                                                                      Math.round(
                                                                          Number(
                                                                              brutto
                                                                          ) -
                                                                              roundDeduction
                                                                      )
                                                                  )
                                                                : "";

                                                        return (
                                                            <section
                                                                key={`team-${teamIdx}`}
                                                                className={`${theme.tableRow} ${theme.border} rounded-xl border p-3 shadow-[0_2px_8px_rgba(31,45,38,0.04)] sm:p-4`}
                                                            >
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="min-w-0">
                                                                        <p
                                                                            className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}
                                                                        >
                                                                            Lag{" "}
                                                                            {teamIdx +
                                                                                1}
                                                                        </p>

                                                                        <h4
                                                                            className={`${theme.textPrimary} mt-0.5 break-words text-base font-semibold`}
                                                                        >
                                                                            {
                                                                                teamName
                                                                            }
                                                                        </h4>
                                                                    </div>

                                                                    {round
                                                                        .scrambleOptions
                                                                        ?.scrambleWithHandicap && (
                                                                        <span
                                                                            className={`${theme.primarySoft} ${theme.heading} shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold`}
                                                                        >
                                                                            Lag-HCP{" "}
                                                                            {formatHandicap(
                                                                                fullTeamHandicap
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="mt-4">
                                                                    <p
                                                                        className={`${theme.textSecondary} mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]`}
                                                                    >
                                                                        {round
                                                                            .scrambleOptions
                                                                            ?.scrambleWithHandicap
                                                                            ? "Brutto och netto"
                                                                            : "Lagets antal slag"}
                                                                    </p>

                                                                    {round
                                                                        .scrambleOptions
                                                                        ?.scrambleWithHandicap ? (
                                                                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                                                                            <div>
                                                                                <input
                                                                                    {...commonInputProps}
                                                                                    name={`brutto-${teamLeaderId}-${roundIdx}`}
                                                                                    placeholder="Brutto"
                                                                                    value={
                                                                                        brutto ??
                                                                                        ""
                                                                                    }
                                                                                    disabled={
                                                                                        !isAdmin
                                                                                    }
                                                                                    aria-label={`Bruttoresultat för ${teamName}`}
                                                                                    className={
                                                                                        inputClassName
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        const parsed =
                                                                                            parseInt(
                                                                                                event
                                                                                                    .target
                                                                                                    .value,
                                                                                                10
                                                                                            );

                                                                                        setBruttoValues(
                                                                                            (
                                                                                                current
                                                                                            ) => ({
                                                                                                ...current,
                                                                                                [bruttoKey]:
                                                                                                    isNaN(
                                                                                                        parsed
                                                                                                    )
                                                                                                        ? ""
                                                                                                        : parsed,
                                                                                            })
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                <p
                                                                                    className={`${theme.textSecondary} mt-1 text-center text-[9px]`}
                                                                                >
                                                                                    Brutto
                                                                                </p>
                                                                            </div>

                                                                            <div className="flex flex-col items-center gap-1">
                                                                                <span
                                                                                    className={`${theme.primarySoft} ${theme.positive} rounded-full px-2 py-1 text-[10px] font-semibold whitespace-nowrap`}
                                                                                >
                                                                                    −
                                                                                    {formatHandicap(
                                                                                        roundDeduction
                                                                                    )}
                                                                                </span>
                                                                                <span
                                                                                    className={`${theme.textSecondary} text-center text-[8px] font-medium leading-tight`}
                                                                                >
                                                                                    Avdrag {round.holes ?? 18} hål
                                                                                </span>
                                                                            </div>

                                                                            <div>
                                                                                <input
                                                                                    name={`${teamLeaderId}-${roundIdx}`}
                                                                                    type="number"
                                                                                    readOnly
                                                                                    value={
                                                                                        netto
                                                                                    }
                                                                                    aria-label={`Nettoresultat för ${teamName}`}
                                                                                    className={
                                                                                        readOnlyInputClassName
                                                                                    }
                                                                                />
                                                                                <p
                                                                                    className={`${theme.textSecondary} mt-1 text-center text-[9px]`}
                                                                                >
                                                                                    Netto
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <input
                                                                            {...commonInputProps}
                                                                            name={`${teamLeaderId}-${roundIdx}`}
                                                                            placeholder="Slag"
                                                                            defaultValue={
                                                                                teamScore
                                                                            }
                                                                            disabled={
                                                                                !isAdmin
                                                                            }
                                                                            aria-label={`Resultat för ${teamName}`}
                                                                            className={
                                                                                inputClassName
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                            </section>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        )}

                                    {isScramble &&
                                        teams.length === 0 && (
                                            <div
                                                className={`${theme.sectionMuted} ${theme.border} rounded-xl border px-4 py-8 text-center`}
                                            >
                                                <p
                                                    className={`${theme.textPrimary} text-sm font-semibold`}
                                                >
                                                    Lagindelningen är
                                                    inte tillgänglig
                                                    ännu
                                                </p>

                                                <p
                                                    className={`${theme.textSecondary} mt-1 text-xs leading-5`}
                                                >
                                                    För en
                                                    positionsbaserad
                                                    scramble skapas
                                                    lagen när
                                                    föregående runda
                                                    har resultat.
                                                </p>
                                            </div>
                                        )}



                                    {tournament.miniGames?.length > 0 && (
                                        <section
                                            className={`${theme.sectionMuted} ${theme.border} mt-4 rounded-xl border p-3 sm:p-4`}
                                        >
                                            <div className="mb-3 flex items-start justify-between gap-3">
                                                <div>
                                                    <p
                                                        className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.2em]`}
                                                    >
                                                        Sidotävlingar
                                                    </p>

                                                    <h4
                                                        className={`${theme.heading} mt-0.5 text-base font-semibold`}
                                                    >
                                                        Sidotävlingar
                                                    </h4>

                                                    <p
                                                        className={`${theme.textSecondary} mt-1 text-[11px] leading-4`}
                                                    >
                                                        Tryck på + när en spelare får en vinst eller poäng.
                                                        Minusknappen rättar snabbt ett feltryck.
                                                    </p>
                                                </div>

                                                <span
                                                    className={`${theme.accentSoft} ${theme.accent} shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold`}
                                                >
                                                    {tournament.miniGames.length}{" "}
                                                    {tournament.miniGames.length === 1
                                                        ? "gren"
                                                        : "grenar"}
                                                </span>
                                            </div>

                                            <div className="grid gap-3 lg:grid-cols-2">
                                                {tournament.miniGames.map((miniGame) => {
                                                    const totalValue = tournament.playerData.reduce(
                                                        (sum, player) => {
                                                            const key = getMiniGameValueKey(
                                                                roundIdx,
                                                                miniGame,
                                                                player.id
                                                            );

                                                            return (
                                                                sum +
                                                                Number(miniGameValues[key] || 0)
                                                            );
                                                        },
                                                        0
                                                    );

                                                    return (
                                                        <article
                                                            key={
                                                                miniGame.id ||
                                                                miniGame.name
                                                            }
                                                            className={`${theme.section} ${theme.border} overflow-hidden rounded-xl border`}
                                                        >
                                                            <div
                                                                className={`${theme.border} flex items-start justify-between gap-3 border-b px-3 py-3 sm:px-4`}
                                                            >
                                                                <div className="min-w-0">
                                                                    <p
                                                                        className={`${theme.textPrimary} break-words text-sm font-semibold`}
                                                                    >
                                                                        {miniGame.name}
                                                                    </p>

                                                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                                                        <span
                                                                            className={`${theme.primarySoft} ${theme.heading} rounded-full px-2 py-0.5 text-[9px] font-semibold`}
                                                                        >
                                                                            Totalt {totalValue}
                                                                        </span>

                                                                        {miniGame.subtract_from_score && (
                                                                            <span
                                                                                className={`${theme.accentSoft} ${theme.accent} rounded-full px-2 py-0.5 text-[9px] font-semibold`}
                                                                            >
                                                                                −{miniGame.deduction_value || 0} slag per poäng
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        resetMiniGame(
                                                                            roundIdx,
                                                                            miniGame
                                                                        )
                                                                    }
                                                                    disabled={!isAdmin || totalValue === 0}
                                                                    className={`${theme.textSecondary} shrink-0 rounded-lg px-2 py-1.5 text-[10px] font-semibold transition hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-35`}
                                                                >
                                                                    Nollställ
                                                                </button>
                                                            </div>

                                                            <div className="divide-y divide-black/[0.06]">
                                                                {tournament.playerData.map(
                                                                    (player) => {
                                                                        const valueKey =
                                                                            getMiniGameValueKey(
                                                                                roundIdx,
                                                                                miniGame,
                                                                                player.id
                                                                            );
                                                                        const value = Number(
                                                                            miniGameValues[
                                                                                valueKey
                                                                            ] || 0
                                                                        );

                                                                        const assignedTeam =
                                                                            tournament.team_mode
                                                                                ? tournament.team_data
                                                                                      ?.assignments?.[
                                                                                      player.id
                                                                                  ]
                                                                                : null;

                                                                        return (
                                                                            <div
                                                                                key={player.id}
                                                                                className="flex items-center gap-3 px-3 py-2.5 sm:px-4"
                                                                            >
                                                                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                                                                    {assignedTeam && (
                                                                                        <span
                                                                                            className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white"
                                                                                            style={{
                                                                                                backgroundColor:
                                                                                                    assignedTeam ===
                                                                                                    "red"
                                                                                                        ? "#9F3A38"
                                                                                                        : "#2F6B4F",
                                                                                            }}
                                                                                        />
                                                                                    )}

                                                                                    <div className="min-w-0">
                                                                                        <p
                                                                                            className={`${theme.textPrimary} truncate text-xs font-semibold sm:text-sm`}
                                                                                            title={player.name}
                                                                                        >
                                                                                            {player.name}
                                                                                        </p>

                                                                                        {value > 0 && (
                                                                                            <p
                                                                                                className={`${theme.textSecondary} mt-0.5 text-[9px]`}
                                                                                            >
                                                                                                {value === 1
                                                                                                    ? "1 registrerad"
                                                                                                    : `${value} registrerade`}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                <div
                                                                                    className={`${theme.borderStrong} grid shrink-0 grid-cols-[42px_50px_42px] overflow-hidden rounded-xl border bg-white sm:grid-cols-[44px_56px_44px]`}
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            changeMiniGameValue(
                                                                                                roundIdx,
                                                                                                miniGame,
                                                                                                player.id,
                                                                                                -1
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            !isAdmin ||
                                                                                            value === 0
                                                                                        }
                                                                                        className={`${theme.textSecondary} flex h-11 items-center justify-center border-r border-black/10 text-xl font-medium transition hover:bg-black/[0.04] active:bg-black/[0.08] disabled:cursor-not-allowed disabled:opacity-30`}
                                                                                        aria-label={`Minska ${miniGame.name} för ${player.name}`}
                                                                                    >
                                                                                        −
                                                                                    </button>

                                                                                    <input
                                                                                        {...commonInputProps}
                                                                                        name={`mini-${roundIdx}-${miniGame.name}-${player.id}`}
                                                                                        value={value}
                                                                                        disabled={!isAdmin}
                                                                                        onChange={(event) =>
                                                                                            setMiniGameValue(
                                                                                                roundIdx,
                                                                                                miniGame,
                                                                                                player.id,
                                                                                                event.target.value
                                                                                            )
                                                                                        }
                                                                                        aria-label={`${miniGame.name} för ${player.name}`}
                                                                                        className={`${theme.textPrimary} h-11 w-full border-0 bg-transparent px-1 text-center text-base font-bold tabular-nums outline-none focus:bg-black/[0.025]`}
                                                                                    />

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            changeMiniGameValue(
                                                                                                roundIdx,
                                                                                                miniGame,
                                                                                                player.id,
                                                                                                1
                                                                                            )
                                                                                        }
                                                                                        disabled={!isAdmin}
                                                                                        className={`${theme.heading} flex h-11 items-center justify-center border-l border-black/10 text-xl font-semibold transition hover:bg-black/[0.04] active:bg-black/[0.08] disabled:cursor-not-allowed disabled:opacity-30`}
                                                                                        aria-label={`Öka ${miniGame.name} för ${player.name}`}
                                                                                    >
                                                                                        +
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    )}

                                    {tournament.rounds.length >
                                        1 && (
                                        <div
                                            className={`${theme.border} mt-4 flex items-center justify-between border-t pt-4`}
                                        >
                                            <button
                                                type="button"
                                                disabled={
                                                    roundIdx === 0
                                                }
                                                onClick={() =>
                                                    setActiveRoundIndex(
                                                        Math.max(
                                                            0,
                                                            roundIdx -
                                                                1
                                                        )
                                                    )
                                                }
                                                className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-xl border px-3 py-2 text-xs font-semibold transition hover:bg-black/[0.025] disabled:cursor-not-allowed disabled:opacity-40`}
                                            >
                                                Föregående
                                            </button>

                                            <span
                                                className={`${theme.textSecondary} text-xs font-medium`}
                                            >
                                                {roundIdx + 1} av{" "}
                                                {
                                                    tournament
                                                        .rounds
                                                        .length
                                                }
                                            </span>

                                            <button
                                                type="button"
                                                disabled={
                                                    roundIdx ===
                                                    tournament
                                                        .rounds
                                                        .length -
                                                        1
                                                }
                                                onClick={() =>
                                                    setActiveRoundIndex(
                                                        Math.min(
                                                            tournament
                                                                .rounds
                                                                .length -
                                                                1,
                                                            roundIdx +
                                                                1
                                                        )
                                                    )
                                                }
                                                className={`${theme.section} ${theme.border} ${theme.heading} rounded-xl border px-3 py-2 text-xs font-semibold transition hover:bg-black/[0.025] disabled:cursor-not-allowed disabled:opacity-40`}
                                            >
                                                Nästa
                                            </button>
                                        </div>
                                    )}
                                </article>
                            );
                        }
                    )}
                </div>

                {isAdmin && (
                    <div
                        className={`${theme.section} ${theme.border} sticky bottom-2 z-30 mx-3 mb-3 flex flex-col gap-2 rounded-xl border p-3 shadow-[0_12px_30px_rgba(31,45,38,0.18)] sm:static sm:m-0 sm:flex-row sm:items-center sm:justify-between sm:rounded-b-2xl sm:rounded-t-none sm:border-x-0 sm:border-b-0 sm:px-6 sm:py-4 sm:shadow-none`}
                    >
                        <div className="min-h-5">
                            {saveMessage && (
                                <p
                                    className={`text-xs font-semibold ${
                                        saveMessage.includes(
                                            "kunde inte"
                                        )
                                            ? theme.negative
                                            : theme.positive
                                    }`}
                                    role="status"
                                >
                                    {saveMessage}
                                </p>
                            )}

                            {!saveMessage && (
                                <p
                                    className={`${theme.textSecondary} text-[10px] sm:text-xs`}
                                >
                                    Alla rundor sparas
                                    samtidigt.
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isSaving}
                            className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-12 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[170px]`}
                        >
                            {isSaving
                                ? "Sparar…"
                                : "Spara resultat"}
                        </button>
                    </div>
                )}
            </form>
        </section>
    );
}